/**
 * Centralized Environment Detection
 *
 * Provides consistent environment detection across web and native platforms.
 * Git branch is the source of truth - VERCEL_ENV is NOT used.
 *
 * Priority order:
 * 1. Runtime: URL hostname (www.dramadetector.com, st.dramadetector.com, localhost)
 * 2. Build time: __DEV__ flag (React Native local development)
 * 3. Build time: Git branch (ONLY source of truth)
 *    - main = production
 *    - Everything else = staging (develop, feature/*, hotfix/*, etc.)
 *
 * Environments:
 * - production: www.dramadetector.com OR main branch
 * - staging: st.dramadetector.com OR any non-main branch (develop, feature/*, etc.)
 * - development: localhost OR __DEV__ flag
 *
 * Note: Feature branches deployed to Cloudflare Pages are still tagged
 * as "staging" in Sentry/PostHog. Only main branch = production.
 */

export type Environment = 'production' | 'staging' | 'development';

/**
 * Get current environment with URL-based detection for web
 */
export function getEnvironment(): Environment {
  // Web platform: Use URL-based detection first
  // Check for browser environment (window + document) instead of Platform.OS
  // This works correctly during SSR/build time on server
  if (typeof window !== 'undefined' && typeof document !== 'undefined') {
    const hostname = window.location.hostname;

    // Production: www.dramadetector.com
    if (hostname === 'www.dramadetector.com') {
      return 'production';
    }

    // Staging: st.dramadetector.com
    if (hostname === 'st.dramadetector.com') {
      return 'staging';
    }

    // Local development: localhost or 127.0.0.1
    if (hostname === 'localhost' || hostname === '127.0.0.1' || hostname.startsWith('192.168.')) {
      return 'development';
    }
  }

  // Fallback to build-time environment variables (works for all platforms)
  // Git branch is the ONLY source of truth for environment
  const GIT_BRANCH = process.env.CF_PAGES_BRANCH || process.env.VERCEL_GIT_COMMIT_REF;

  // __DEV__ flag (React Native local development)
  if (typeof __DEV__ !== 'undefined' && __DEV__) {
    return 'development';
  }

  // Git branch = source of truth
  // main branch = production (deployed to www.dramadetector.com)
  if (GIT_BRANCH === 'main') {
    return 'production';
  }

  // Everything else = staging (develop, feature/*, hotfix/*, release/*, etc.)
  // This includes:
  // - develop branch (deployed to st.dramadetector.com)
  // - feature branches (preview deployments)
  // - hotfix branches (emergency fixes)
  // - release branches (pre-production testing)
  // - personal branches (experiments)
  //
  // Note: Even if deployed to a production Cloudflare Pages slot, non-main branches
  // are always tagged as "staging" in Sentry/PostHog
  return 'staging';
}

/**
 * Check if running in production
 */
export function isProduction(): boolean {
  return getEnvironment() === 'production';
}

/**
 * Check if running in staging
 */
export function isStaging(): boolean {
  return getEnvironment() === 'staging';
}

/**
 * Check if running in development
 */
export function isDevelopment(): boolean {
  return getEnvironment() === 'development';
}

/**
 * Get Sentry environment string
 * (maintains backward compatibility with existing analytics code)
 */
export function getSentryEnvironment(): Environment {
  return getEnvironment();
}
